import { omit } from 'lodash';
import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';

import { BalanceSheetConfigurationPopper_BalanceSheetViewConfigurationFragment } from '../BalanceSheetConfigurationPopper/graphql/BalanceSheetConfigurationPopper.generated';
import { RowData } from './BalanceSheetTable.constants';

interface SelectedEntitiesRow {
  entityIds: string[];
  row: RowData;
}

interface SelectedLiabilityRow {
  liabilityId: string;
  row: RowData;
}

export type ViewConfiguration = Omit<
  BalanceSheetConfigurationPopper_BalanceSheetViewConfigurationFragment,
  'id' | '__typename'
> & {
  /** Display name for the balance sheet slide (presentation mode only) */
  displayName?: string;
};

interface BalanceSheetTableContextType {
  viewConfigurationId: string | null;
  selectedEntitiesRow: SelectedEntitiesRow | null;
  setSelectedEntitiesRow: (row: SelectedEntitiesRow | null) => void;
  selectedLiabilityRow: SelectedLiabilityRow | null;
  setSelectedLiabilityRow: (row: SelectedLiabilityRow | null) => void;
  viewConfiguration: ViewConfiguration | null;
  setViewConfiguration: (viewConfig: ViewConfiguration) => void;
  loading: boolean;
  renderWhiteLabeled: boolean;
  setRenderWhiteLabeled: (render: boolean) => void;
  /** Draft column order from the config panel, used for live preview. Null = use server order. */
  draftColumnOrder: string[] | null;
  setDraftColumnOrder: (order: string[] | null) => void;
  /** Draft hidden columns from the config panel, used for live preview. Null = use server isHidden. */
  draftHiddenColumns: Set<string> | null;
  setDraftHiddenColumns: (hidden: Set<string> | null) => void;
  /**
   * Maximum number of data columns that fit in the table's measured width.
   * Set by BalanceSheetTable when in presentation mode. Null = no limit (in-app view).
   */
  maxVisibleColumns: number | null;
  setMaxVisibleColumns: (max: number | null) => void;
}

const BalanceSheetTableContext = createContext<
  BalanceSheetTableContextType | undefined
>(undefined);

interface BalanceSheetTableProviderProps {
  children: ReactNode;
  viewConfiguration: BalanceSheetConfigurationPopper_BalanceSheetViewConfigurationFragment | null;
  loading: boolean;
}

export function BalanceSheetTableProvider({
  children,
  viewConfiguration: initialViewConfiguration,
  loading,
}: BalanceSheetTableProviderProps) {
  // Split off the id because the viewConfig is interacted with directly
  // from a form, and the id is needed for updates.
  const { id: propId, viewConfig: propConfig } = useMemo(() => {
    return {
      id: initialViewConfiguration?.id ?? null,
      // Guard against omit(null) returning {} instead of null — an empty
      // object is truthy and would cause getViewConfigFilteredColumns to
      // treat showInEstate/showOutOfEstate as false, hiding all columns.
      viewConfig: initialViewConfiguration
        ? omit(initialViewConfiguration, 'id', '__typename')
        : null,
    };
  }, [initialViewConfiguration]);

  const [selectedRow, setSelectedRow] = useState<SelectedEntitiesRow | null>(
    null
  );
  const [selectedLiabilityRow, setSelectedLiabilityRow] =
    useState<SelectedLiabilityRow | null>(null);
  const [viewConfiguration, setViewConfiguration] =
    useState<ViewConfiguration | null>(propConfig);
  const [renderWhiteLabeled, setRenderWhiteLabeled] = useState(false);
  const [draftColumnOrder, setDraftColumnOrder] = useState<string[] | null>(
    null
  );
  const [draftHiddenColumns, setDraftHiddenColumns] =
    useState<Set<string> | null>(null);
  const [maxVisibleColumns, setMaxVisibleColumns] = useState<number | null>(
    null
  );

  // Sync viewConfiguration from prop during render so consumers see the
  // correct value on the same render cycle the prop changes — not one
  // render late (which would happen with useEffect).
  //
  // Skip transient null props while loading. Apollo refetches can briefly
  // produce data === undefined, which propagates as viewConfiguration === null
  // here. Treating that as a real config change would cause downstream state
  // (e.g. expandedSections in BalanceSheetTable) to flicker.
  const [prevPropId, setPrevPropId] = useState(propId);
  if (propId !== prevPropId && !(propId === null && loading)) {
    setPrevPropId(propId);
    setViewConfiguration(propConfig);
  }

  return (
    <BalanceSheetTableContext.Provider
      value={{
        viewConfigurationId: propId,
        selectedEntitiesRow: selectedRow,
        setSelectedEntitiesRow: setSelectedRow,
        selectedLiabilityRow,
        setSelectedLiabilityRow,
        setViewConfiguration,
        viewConfiguration,
        renderWhiteLabeled,
        setRenderWhiteLabeled,
        draftColumnOrder,
        setDraftColumnOrder,
        draftHiddenColumns,
        setDraftHiddenColumns,
        maxVisibleColumns,
        setMaxVisibleColumns,
        loading,
      }}
    >
      {children}
    </BalanceSheetTableContext.Provider>
  );
}

export function useBalanceSheetTableContext() {
  return useGuardedContext(
    BalanceSheetTableContext,
    'BalanceSheetTableProvider'
  );
}

export function useUnguardedBalanceSheetTableContext() {
  return useContext(BalanceSheetTableContext);
}
